import { PublicKey } from '@solana/web3.js';
import { ENUM_UTILS, COMMON_UI_UTILS } from '@drift/common';
import { OrderActionExplanation, OrderAction } from '@drift-labs/sdk';
import { getTradeInfoFromOrderActionRecord } from 'src/utils/tradeRecord';
import { getOrderStatusString } from 'src/utils/order';
import {
	abbreviateAddress,
	splitByCapitalLetters,
	lowerCaseNonFirstWords,
} from 'src/utils/strings';
import { DEFAULT_PUBLIC_KEY } from 'src/@types/types';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import { isEntirePositionOrder } from 'src/utils/fullPositionOrders';
import useOrderActionHistory from 'src/hooks/historyData/useOrderActionHistory';
import { NON_FILL_ACTIONS } from './constants';

interface UseOrderRowDataProps {
	orderId: number;
	expanded: boolean;
}

export function useOrderRowData({ orderId, expanded }: UseOrderRowDataProps) {
	const currentAccountPubKey = useDriftAccountStore(
		(s) => s.getCurrentUserAccount()?.pubKey
	);
	const { orderActionRecords: records, isLoading } = useOrderActionHistory({
		orderId,
		currentAccountPubKey,
		enabled: expanded,
	});

	const processedRecords =
		records?.length && currentAccountPubKey
			? [...records]
					.sort((a, b) => b.ts.toNumber() - a.ts.toNumber())
					.map((record) => {
						const tradeInfo = getTradeInfoFromOrderActionRecord(
							currentAccountPubKey as PublicKey,
							record
						);
						const date = new Date(tradeInfo.ts.toNumber() * 1000);

						const avgPriceBn = COMMON_UI_UTILS.calculateAverageEntryPrice(
							tradeInfo.quoteAssetAmountFilled,
							tradeInfo.baseAssetAmountFilled
						);
						const orderedIsEntire = isEntirePositionOrder(
							tradeInfo.baseAssetAmount
						);

						const isPerp = 'perp' === tradeInfo.marketType;
						const isDefaultCounterparty = (
							tradeInfo.counterparty ?? DEFAULT_PUBLIC_KEY
						).equals(DEFAULT_PUBLIC_KEY);
						const counterpartyStr = tradeInfo.counterparty?.toString();
						const filledBy = isDefaultCounterparty
							? isPerp
								? 'Drift AMM'
								: ENUM_UTILS.match(
											record.actionExplanation,
											OrderActionExplanation.ORDER_FILLED_WITH_SERUM
									  )
									? 'OpenBook'
									: 'Phoenix'
							: tradeInfo.counterparty
								? abbreviateAddress(tradeInfo.counterparty)
								: '';

						const status = getOrderStatusString(
							record.action,
							tradeInfo.totalBaseAmountFilled,
							tradeInfo.baseAssetAmount
						);

						const isCancel = ENUM_UTILS.match(
							record.action,
							OrderAction.CANCEL
						);
						const cancelExplanation = isCancel
							? ENUM_UTILS.match(
									record.actionExplanation,
									OrderActionExplanation.NONE
								)
								? 'Cancelled by user'
								: lowerCaseNonFirstWords(
										splitByCapitalLetters(
											ENUM_UTILS.toStr(record.actionExplanation)
										)
									)
							: undefined;

						const isNonFillAction = NON_FILL_ACTIONS.includes(status);

						return {
							record,
							tradeInfo,
							date,
							avgPriceBn,
							orderedIsEntire,
							isPerp,
							isDefaultCounterparty,
							counterpartyStr,
							filledBy,
							status,
							isCancel,
							cancelExplanation,
							isNonFillAction,
						};
					})
			: [];

	return {
		processedRecords,
		isLoading,
		hasData: !!records?.length && !!currentAccountPubKey,
	};
}
